import { localizationStore } from '@/Controllers/Localization/LocalizationStore';
import { AbstractViewController } from '@/Controllers/Abstract/AbstractViewController';
import { DEFAULT_LOCALE, resolveLocale } from '@standardnotes/i18n';
import { getWebLocaleService } from '@/Controllers/Localization/WebLocaleService';
import { configureDateLocale } from '@/Utils/DateLocale';
import { syncDesktopMainProcessLocalization } from '@/Application/Device/SyncDesktopMainProcessLocalization';
import { syncMobileNativeLocalization } from '@/Application/Device/SyncMobileNativeLocalization';
import { clearPersistedLocale, getPersistedLocale, persistLocale, setPendingLocaleSwitch, takePendingLocaleSwitch, } from '@/Utils/LocalePersistence';
import { addToast, ToastType } from '@standardnotes/toast';
import { ElementIds } from '@/Constants/ElementIDs';
import { RouteType } from '@standardnotes/ui-services';
import { c } from 'ttag';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { ApplicationEvent, PrefDefaults, PrefKey, } from '@standardnotes/snjs';
export class LocalizationController extends AbstractViewController {
    constructor(featuresController, getSavedLocale, preferences, preferencesController, routeService, eventBus) {
        super(eventBus);
        this.featuresController = featuresController;
        this.getSavedLocale = getSavedLocale;
        this.preferences = preferences;
        this.preferencesController = preferencesController;
        this.routeService = routeService;
        this.labsSpotlightOpen = false;
        this.labsSpotlightPrefLoaded = false;
        this.initializedWithLocalizationDisabled = false;
        this.isTogglingLocalization = false;
        this.dismissLabsSpotlight = () => {
            this.setLabsSpotlightOpen(false);
            void this.preferences.setValue(PrefKey.HasSeenLocalizationLabsSpotlight, true);
        };
        this.openLanguageSettingsFromLabsSpotlight = () => {
            this.dismissLabsSpotlight();
            this.preferencesController.openPreferencesAndScrollToSection(ElementIds.LanguagePreferencesSection, 'general');
        };
        makeObservable(this, {
            labsSpotlightOpen: observable,
            dismissLabsSpotlight: action,
            openLanguageSettingsFromLabsSpotlight: action,
        });
        eventBus.addEventHandler(this, ApplicationEvent.FeaturesAvailabilityChanged);
        eventBus.addEventHandler(this, ApplicationEvent.LocalDataLoaded);
        eventBus.addEventHandler(this, ApplicationEvent.PreferencesChanged);
        eventBus.addEventHandler(this, ApplicationEvent.SignedOut);
    }
    async handleEvent(event) {
        switch (event.type) {
            case ApplicationEvent.LocalDataLoaded:
                this.labsSpotlightPrefLoaded = true;
                await this.initializeLocalization();
                this.setLabsSpotlightOpen(this.shouldShowLabsSpotlight());
                break;
            case ApplicationEvent.FeaturesAvailabilityChanged:
            case ApplicationEvent.PreferencesChanged:
                await this.initializeLocalization();
                this.setLabsSpotlightOpen(this.shouldShowLabsSpotlight());
                break;
            case ApplicationEvent.SignedOut:
                clearPersistedLocale();
                break;
        }
    }
    hasSeenLocalizationLabsSpotlight() {
        return this.preferences.getValue(PrefKey.HasSeenLocalizationLabsSpotlight, PrefDefaults[PrefKey.HasSeenLocalizationLabsSpotlight]);
    }
    shouldShowLabsSpotlight() {
        if (!this.labsSpotlightPrefLoaded) {
            return false;
        }
        const routeType = this.routeService.getRoute().type;
        return (this.featuresController.isLocalizationFeatureAvailable() &&
            !this.featuresController.isLocalizationEnabled() &&
            !this.hasSeenLocalizationLabsSpotlight() &&
            routeType !== RouteType.Purchase &&
            routeType !== RouteType.Settings);
    }
    setLabsSpotlightOpen(open) {
        runInAction(() => {
            this.labsSpotlightOpen = open;
        });
    }
    async changeLocaleAndReload(locale) {
        if (locale === localizationStore.currentLocale && locale === getPersistedLocale()) {
            return false;
        }
        if (!this.featuresController.isLocalizationEnabled()) {
            return false;
        }
        let resolvedLocale;
        try {
            resolvedLocale = await this.loadLocale(locale);
        }
        catch (error) {
            console.error('Failed to load locale before reload', error);
            addToast({
                type: ToastType.Error,
                message: c('B6.Preferences.General.Language.Error').t `Could not load the selected language.`,
            });
            return false;
        }
        persistLocale(resolvedLocale);
        try {
            await this.preferences.setValue(PrefKey.Locale, resolvedLocale);
        }
        catch (error) {
            console.error('Failed to sync locale preference', error);
        }
        this.reloadAndShowToast(resolvedLocale);
        return true;
    }
    async toggleLocalization(waitBeforeReload) {
        this.isTogglingLocalization = true;
        this.featuresController.toggleLocalization();
        const locale = this.featuresController.isLocalizationEnabled()
            ? await this.enableLocalization()
            : await this.disableLocalization();
        await waitBeforeReload;
        if (locale) {
            this.reloadAndShowToast(locale);
        }
        else {
            window.location.reload();
        }
    }
    async enableLocalization() {
        try {
            const locale = await this.loadLocale(this.getSavedLocale());
            persistLocale(locale);
            return locale;
        }
        catch (error) {
            console.error('Failed to load locale before reload', error);
            return undefined;
        }
    }
    async disableLocalization() {
        clearPersistedLocale();
        try {
            await this.preferences.setValue(PrefKey.Locale, PrefDefaults[PrefKey.Locale]);
        }
        catch (error) {
            console.error('Failed to clear locale preference', error);
        }
        return DEFAULT_LOCALE;
    }
    reloadAndShowToast(locale) {
        if (locale !== localizationStore.currentLocale) {
            setPendingLocaleSwitch(locale);
        }
        window.location.reload();
    }
    async showLocaleSwitchedToast(locale) {
        var _a;
        let languageName = locale;
        try {
            languageName = (_a = (await getWebLocaleService().getAvailableLocales())[locale]) !== null && _a !== void 0 ? _a : locale;
        }
        catch (error) {
            console.error('Failed to load locale catalog', error);
        }
        addToast({
            type: ToastType.Success,
            message: c('B6.Preferences.General.Language.Info').t `Language switched to ${languageName}`,
        });
    }
    async loadLocale(locale) {
        const localeService = getWebLocaleService();
        const catalog = await localeService.getAvailableLocales();
        const resolvedLocale = resolveLocale({
            savedLocale: locale,
            availableLocales: Object.keys(catalog),
        });
        await localeService.loadAndActivateLocale(resolvedLocale);
        return resolvedLocale;
    }
    updatePersistedLocale(localizationEnabled, locale) {
        const persistedLocale = getPersistedLocale();
        if (!localizationEnabled) {
            if (!persistedLocale) {
                return false;
            }
            clearPersistedLocale();
            return true;
        }
        if (persistedLocale === locale) {
            return false;
        }
        persistLocale(locale);
        return true;
    }
    async initializeLocalization() {
        if (this.isTogglingLocalization) {
            return;
        }
        const localizationEnabled = this.featuresController.isLocalizationEnabled();
        if (!localizationEnabled && this.initializedWithLocalizationDisabled) {
            return;
        }
        this.initializedWithLocalizationDisabled = !localizationEnabled;
        const localeService = getWebLocaleService();
        const activeLocale = localeService.getCurrentLocale();
        try {
            const catalog = localizationEnabled ? await localeService.getAvailableLocales() : {};
            const locale = localizationEnabled
                ? resolveLocale({ savedLocale: this.getSavedLocale(), availableLocales: Object.keys(catalog) })
                : DEFAULT_LOCALE;
            const persistedLocaleChanged = this.updatePersistedLocale(localizationEnabled, locale);
            if (locale !== activeLocale && (persistedLocaleChanged || !localizationEnabled)) {
                this.reloadAndShowToast(locale);
                return;
            }
            localizationStore.setAvailableLocales(catalog);
        }
        catch (error) {
            console.error('Failed to load locale catalog', error);
            localizationStore.setAvailableLocalesFailedToLoad();
        }
        if (takePendingLocaleSwitch() === activeLocale) {
            void this.showLocaleSwitchedToast(activeLocale);
        }
        await this.syncDateFormatting(activeLocale);
        localizationStore.setCurrentLocale(activeLocale);
        this.syncNativeShellLocalization(localizationEnabled, activeLocale);
    }
    syncNativeShellLocalization(localizationEnabled, locale) {
        const state = {
            localizationEnabled,
            locale: localizationEnabled ? locale : undefined,
        };
        syncDesktopMainProcessLocalization(state);
        syncMobileNativeLocalization(state);
    }
    async syncDateFormatting(appLocale) {
        await configureDateLocale({
            appLocale,
            localizationEnabled: this.featuresController.isLocalizationEnabled(),
        });
    }
}
